import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

/* --------------------------------- Email Send ------------------------------ */

export interface EmailRequest {
  voucherId: string;
  receiverList?: string[];
}

export interface EmailResponse {
  success: boolean;
  message: string;
  data?: any;
}

/* --------------------------------- SMS Send ------------------------------ */

export interface SmsPayload {
  voucherId: string;
  receiverList?: string[];
}

export interface SmsRequest {
  voucherId: string;
  receiverList?: string[];
}

export interface SmsResponse {
  success: boolean;
  message: string;
  data?: any;
}

/* --------------------------------- Whatsapp Send ------------------------------ */

export interface WhatsappRequest {
  voucherId: string
}

export interface WhatsappResponse {
  success: boolean
  message: string
  data?: any
}

/* --------------------------------- Survey Email Send ------------------------------ */

export interface SurveyEmailRequest {
  voucherId: string;
  receiverList?: string[];
}

export interface SurveyEmailResponse {
  success: boolean;
  message: string;
  data?: any;
}

export const sendEmailNotification = createAsyncThunk(
  "notification/sendEmail", 
  async ({ voucherId, receiverList }: EmailRequest, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken");

    try {
      const requestData: any = {
        voucherId,
      };

      // Add receiverList only if it has one element
      if (receiverList && receiverList.length === 1) {
        requestData.receiverList = receiverList;
      }

      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/notifications/email/send`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        method: "POST",
      });

      return response.data as EmailResponse;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message
          ? error.response.data.message
          : "Failed to send email notification"
      );
    }
  }
);

export const sendSmsNotification = createAsyncThunk(
  "notification/sendSms", 
  async ({ voucherId, receiverList }: SmsRequest, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken");

    try {
      const requestData: any = {
        voucherId,
      };

      // Add receiverList only if it has one element
      if (receiverList && receiverList.length === 1) {
        requestData.receiverList = receiverList;
      }

      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/notifications/sms/send`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        method: "POST",
      });

      return response.data as SmsResponse;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message
          ? error.response.data.message
          : "Failed to send SMS notification"
      );
    }
  }
);

export const sendWhatsappNotification = createAsyncThunk(
  "notification/sendWhatsapp",
  async ({ voucherId }: WhatsappRequest, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken")

    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/notifications/whatsapp/send`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: {
          voucherId,
        },
        method: "POST",
      });

      return response.data as WhatsappResponse;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message
          ? error.reponse.data.message
          : "Failed to send Whatsapp notification"
      );
    }
  }
);

export const sendSurveyEmailNotification = createAsyncThunk(
  "notification/sendSurveyEmail", 
  async ({ voucherId, receiverList }: SurveyEmailRequest, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken");

    try {
      const requestData: any = {
        voucherId,
      };

      // Add receiverList only if it has one element
      if (receiverList && receiverList.length === 1) {
        requestData.receiverList = receiverList;
      }

      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/notifications/email/send-survey`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        method: "POST",
      });

      return response.data as SurveyEmailResponse;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message 
          ? error.response.data.message 
          : "Failed to send survey email notification"
      );
    }
  }
);

/* --------------------------------- Main State ------------------------------ */

interface NotificationState {
  emailResponse: EmailResponse | null;
  emailError: string | null;
  emailLoading: boolean;
  smsResponse: SmsResponse | null;
  smsError: string | null;
  smsLoading: boolean;
  whatsappResponse: WhatsappResponse | null;
  whatsappError: string | null;
  whatsappLoading: boolean;
  surveyEmailResponse: SurveyEmailResponse | null;
  surveyEmailError: string | null;
  surveyEmailLoading: boolean;
}

const initialState: NotificationState = {
  emailResponse: null,
  emailError: null,
  emailLoading: false,
  smsResponse: null,
  smsError: null,
  smsLoading: false,
  whatsappResponse: null,
  whatsappError: null,
  whatsappLoading: false,
  surveyEmailResponse: null,
  surveyEmailError: null,
  surveyEmailLoading: false,
};

const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    clearEmailNotification(state) {
      state.emailResponse = null;
      state.emailLoading = false;
      state.emailError = null;
    },
    clearSmsNotification(state) {
      state.smsResponse = null;
      state.smsLoading = false;
      state.smsError = null;
    },
    clearWhatsappNotification(state) {
      state.whatsappResponse = null;
      state.whatsappLoading = false;
      state.whatsappError = null;
    },
    clearSurveyEmailNotification(state) {
      state.surveyEmailResponse = null;
      state.surveyEmailLoading = false;
      state.surveyEmailError = null;
    },
    clearAllNotifications(state) {
      state.emailResponse = null;
      state.emailLoading = false;
      state.emailError = null;
      state.smsResponse = null;
      state.smsLoading = false;
      state.smsError = null;
      state.whatsappResponse = null;
      state.whatsappLoading = false;
      state.whatsappError = null;
      state.surveyEmailResponse = null;
      state.surveyEmailLoading = false;
      state.surveyEmailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Email Send
      .addCase(sendEmailNotification.pending, (state) => {
        state.emailLoading = true;
        state.emailError = null;
      })
      .addCase(sendEmailNotification.fulfilled, (state, action: PayloadAction<EmailResponse>) => {
          state.emailLoading = false;
          state.emailResponse = action.payload;
      })
      .addCase(sendEmailNotification.rejected, (state, action) => {
        state.emailLoading = false;
        state.emailError = action.payload as string || "Failed to send email notification";
      })
      // SMS Send
      .addCase(sendSmsNotification.pending, (state) => {
        state.smsLoading = true;
        state.smsError = null;
      })
      .addCase(sendSmsNotification.fulfilled, (state, action: PayloadAction<SmsResponse>) => {
          state.smsLoading = false;
          state.smsResponse = action.payload;
      })
      .addCase(sendSmsNotification.rejected, (state, action) => {
        state.smsLoading = false;
        state.smsError = action.payload as string || "Failed to send SMS notification";
      })
      // Whatsapp Send
      .addCase(sendWhatsappNotification.pending, (state) => {
        state.whatsappLoading = true;
        state.whatsappError = null;
      })
      .addCase(
        sendWhatsappNotification.fulfilled, (state, action: PayloadAction<WhatsappResponse>) => {
          state.whatsappLoading = false;
          state.whatsappResponse = action.payload;
      })
      .addCase(sendWhatsappNotification.rejected, (state, action) => {
        state.whatsappLoading = false;
        state.whatsappError = action.payload as string || "Failed to send Whatsapp notification";
      })
      // Survey Email Send
      .addCase(sendSurveyEmailNotification.pending, (state) => {
        state.surveyEmailLoading = true;
        state.surveyEmailError = null;
      })
      .addCase(sendSurveyEmailNotification.fulfilled, (state, action: PayloadAction<SurveyEmailResponse>) => {
        state.surveyEmailLoading = false;
        state.surveyEmailResponse = action.payload;
      })
      .addCase(sendSurveyEmailNotification.rejected, (state, action) => {
        state.surveyEmailLoading = false;
        state.surveyEmailError = action.payload as string || "Failed to send survey email notification";
      });
  },
});

export const { clearEmailNotification, clearSmsNotification, clearSurveyEmailNotification, clearWhatsappNotification, clearAllNotifications } = notificationSlice.actions;

export default notificationSlice.reducer;
